import {
    GoogleMap,
    useJobStory,
    WorkSiteMarker,
    MapButton,
    MapControl,
    TypeAheadInput,
    theme,
    AutocompleteTypes,
    Ripple,
    useMap,
    DeviceGeolocationStatus,
    AutocompleteOption,
} from 'rwl-ui';
import { PluginWorkSite } from 'rwl-shared/dist/models';
import { DEFAULT_ICON_COLOR } from 'rwl-shared/dist/models/plugin/Plugin';
import { When } from 'react-if';
import { Config } from '../../fetchers/ConfigFetcher/useConfig';
import { determineIconForWorkSite } from './utils';
import { useGeolocation } from '../../hooks/useGeolocation';
import {
    faClock,
    faLocationArrow,
    faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useState } from 'react';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutocomplete';

export function MapRenderer({
    config,
    workSites,
    showMovedButton,
    clearShowMovedButton,
    onClickSearch,
    brandColor,
}: {
    config: Config;
    workSites: PluginWorkSite[];
    showMovedButton: boolean;
    clearShowMovedButton: () => void;
    onClickSearch: () => void;
    brandColor?: string;
}): JSX.Element {
    const { jobStoryId, setJobStoryId } = useJobStory();
    const {
        isLoading: isLoadingGeolocation,
        requestGeolocation,
        status,
    } = useGeolocation();
    const { fitBounds, map } = useMap();
    const [featureLayer, setFeatureLayer] =
        useState<google.maps.FeatureLayer | null>(null);
    const {
        setQuery,
        suggestions,
        recentResults,
        addRecentResult,
        setSelectedPlaceId,
        clearSelectedPlaceId,
        selectedPlaceDetails,
    } = usePlacesAutocomplete();

    const clearFeatureLayer = () => {
        if (featureLayer) {
            featureLayer.style = undefined;
        }
    };

    useEffect(() => {
        if (selectedPlaceDetails && map) {
            // Move the map viewport
            fitBounds(selectedPlaceDetails.geometry.viewport);
            // Get rid of the Search Here button if it exists
            clearShowMovedButton();
            // Add place geometry to the map
            clearFeatureLayer();
            const layerType = selectedAreaTypeToFeatureType(
                selectedPlaceDetails.types ?? []
            );
            if (layerType) {
                const newFeatureLayer = map.getFeatureLayer(layerType);
                newFeatureLayer.style = (
                    option: google.maps.FeatureStyleFunctionOptions
                ) => {
                    // @ts-ignore
                    return option.feature.placeId ===
                        selectedPlaceDetails.place_id
                        ? {
                              fillColor: brandColor ?? 'blue',
                              fillOpacity: 0.3,
                              strokeWeight: 0,
                          }
                        : undefined;
                };
                setFeatureLayer(newFeatureLayer);
            }
        }
    }, [selectedPlaceDetails, map]);

    const initialConfig = {
        mapId: '20e342d1468e6199',
        center:
            config?.account?.geolocation?.lat &&
            config?.account?.geolocation?.lng
                ? {
                      lat: config.account.geolocation.lat,
                      lng: config.account.geolocation.lng,
                  }
                : ({} as google.maps.MapOptions['center']),
        maxZoom: config?.account?.mapSettings?.maxZoom ?? 15,
    };

    const locationSuggestions: AutocompleteOption[] = [
        ...recentResults.map((r) => ({
            type: AutocompleteTypes.RECENT,
            key: r.place_id || r.description,
            label: r.description,
            icon: faClock,
            onSelected: () => {
                clearFeatureLayer();
                setSelectedPlaceId(r.place_id);
            },
        })),
        ...suggestions.map((s) => ({
            type: AutocompleteTypes.SUGGESTION,
            key: s.place_id || s.description,
            label: s.description,
            icon: faLocationDot,
            onSelected: () => {
                clearFeatureLayer();
                addRecentResult(s);
                setSelectedPlaceId(s.place_id);
            },
        })),
    ];

    if (status !== DeviceGeolocationStatus.Unavailable) {
        locationSuggestions.unshift({
            type: AutocompleteTypes.CURRENT,
            key: 'current',
            label: 'Current location',
            icon: faLocationArrow,
            onSelected: () => {
                clearFeatureLayer();
                requestGeolocation(15);
            },
        });
    }

    const iconSymbol = determineIconForWorkSite(
        config?.account?.mapSettings?.icon
    );

    const color = config?.account?.mapSettings?.color || DEFAULT_ICON_COLOR;

    return (
        <GoogleMap initialConfig={initialConfig}>
            <MapControl
                position={'TOP'}
                style={{
                    left: 0,
                    right: 0,
                    padding: 'var(--rwl-spacing-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '16px',
                    fontFamily: theme.typography.fontFamily,
                    zIndex: 10,
                }}
            >
                <TypeAheadInput
                    prefilledValue={selectedPlaceDetails?.formatted_address}
                    suggestions={locationSuggestions}
                    onTyping={(value) => {
                        if (value.length > 3) {
                            setQuery(value);
                        }
                    }}
                    onClear={() => {
                        clearSelectedPlaceId();
                        clearFeatureLayer();
                    }}
                    variant="light"
                />
                <When condition={showMovedButton}>
                    <MapButton
                        style={{
                            borderRadius: '36px',
                            marginTop: 'var(--rwl-spacing-md)',
                        }}
                        onClick={() => {
                            clearFeatureLayer();
                            onClickSearch();
                        }}
                    >
                        Search this area
                    </MapButton>
                </When>
            </MapControl>
            {workSites.map((workSite) => (
                <WorkSiteMarker
                    key={workSite.id}
                    workSite={workSite as PluginWorkSite}
                    onClick={() => setJobStoryId(workSite.id)}
                    state={workSite.id === jobStoryId ? 'selected' : undefined}
                    style={{
                        color,
                        iconSymbol,
                    }}
                />
            ))}
            <When condition={isLoadingGeolocation}>
                <MapControl
                    position="CENTER"
                    style={{
                        inset: '0',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '16px',
                        fontFamily: theme.typography.fontFamily,
                        zIndex: 20,
                    }}
                >
                    <Ripple />
                </MapControl>
            </When>
        </GoogleMap>
    );
}

function selectedAreaTypeToFeatureType(
    types: string[]
): google.maps.FeatureType | undefined {
    // NOTE TO FUTURE SELF, if you get google map errors about one of these feature types not
    // being available.  Log into Google Cloud Console and enable the feature type in the
    // MAP STYLING section
    const preferenceOrder = [
        google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_1,
        google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_2,
        google.maps.FeatureType.LOCALITY,
        google.maps.FeatureType.POSTAL_CODE,
    ];

    for (const type of preferenceOrder) {
        if (types.includes(type.toLowerCase())) {
            return type;
        }
    }

    return;
}
